import React, { type ReactNode } from 'react'
import {
  ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS,
  getIntegrationDisplayName,
  getIntegrationShortDisplayName,
  IntegrationServiceId,
} from '../../../common/integrations'
import { DefaultOnboardingFlowStepId, Capability } from '../../../models/Onboarding'

export const CONTACTS_EMPTY_STATE_COPIES = {
  crm: {
    icon: <i className='bi bi-people' />,
    headerTitle: 'Connect your CRM',
    headerSubtitle: 'Zocks syncs your client data, so your AI Assistant can use it to prepare for meetings and personalize notes and follow-ups.',
  },
  planning: {
    icon: <i className='bi bi-graph-up' />,
    headerTitle: 'Connect a Financial Planning tool',
    headerSubtitle:
      'Your enterprise uses financial planning tools for client data. Connect yours and your AI Assistant will use it to prepare for meetings and personalize notes and follow-ups.',
  },
  withoutSync: {
    icon: <i className='bi bi-people' />,
    headerTitle: 'Sync your contacts',
    headerSubtitle: 'Sync your contacts, so your AI Assistant can use them to prepare for meetings and personalize notes and follow-ups.',
  },
}

export const UPCOMING_EMPTY_STATE_COPIES = {
  icon: <i className='bi bi-calendar2' />,
  headerTitle: 'Connect your calendar',
  headerSubtitle: 'Zocks syncs your upcoming meetings, so your AI Assistant is in the right place at the right time — no setup before each call.',
  secondaryServiceIdsLabel: 'or use a CRM-linked calendar',
}

export const FINANCIAL_PLANNING_BANNER_COPIES = {
  title: 'Connect a Financial Planning tool',
  subtitle:
    'Zocks pulls client financial data into your meeting prep and syncs details captured in meetings back to your planning\ntool — so your plans stay current without extra work.',
}

export const EMAIL_BANNER_COPIES = {
  title: 'Connect your email',
  subtitle: 'Zocks uses your email to draft follow-ups after meetings and personalize meeting prep.\nYou review everything before it sends.',
}

export const ZOOM_BANNER_COPIES = {
  title: 'Connect Zoom',
  subtitle: 'Integrate Zocks with Zoom to automatically capture meeting results,\nstreamline workflow and enhance productivity.',
}

const CALENDAR_COMPLETED_BULLETS = [
  'Sync your meetings effortlessly',
  'Join your Zocks AI Assistant to your meetings',
  'You can start your call from your calendar',
]
const CRM_COMPLETED_BULLETS = [
  'Client profiles stay up to date automatically',
  'Preserve full field-level history of changes and their source',
  'Powering Client Intelligence, AI features, and Analytics',
]
const ZOOM_COMPLETED_BULLETS = [
  'Join your AI Assistant automatically to meetings',
  'Streamlined consent tracking',
  "Zocks follows a strict 'No Recording' policy",
]

export interface OnboardingStepLink {
  label: string
  to: string
}

export interface OnboardingStepCopies {
  onboardingScreenDisplayOrder: number
  capability: Capability
  pageSize?: number
  secondaryServiceIdsLabel?: string
  onboardingStepNotification?: ReactNode
  icon: ReactNode
  pendingOnboardingListTitle: string
  pendingHeaderTitle: string
  pendingOnboardingListDescription: string
  pendingHeaderSubtitle: string
  completedOnboardingListTitle: string
  completedOnboardingListDescription: string
  completedBullets?: string[]
  completedLinks?: OnboardingStepLink[]
}

export const ONBOARDING_STEP_COPIES: Record<DefaultOnboardingFlowStepId, OnboardingStepCopies> = {
  [DefaultOnboardingFlowStepId.CALENDAR_SOURCE]: {
    onboardingScreenDisplayOrder: 1,
    capability: Capability.CALENDAR_SOURCE,
    secondaryServiceIdsLabel: 'or use a CRM-linked calendar',
    icon: <i className='bi bi-calendar2' />,
    pendingOnboardingListTitle: 'Connect your calendar',
    pendingHeaderTitle: 'Connect your calendar',
    pendingOnboardingListDescription: 'Manage your meetings and control which to join by your AI Assistant',
    pendingHeaderSubtitle: 'Zocks syncs your upcoming meetings, so your AI Assistant is in the right place at the right time — no setup before each call.',
    completedOnboardingListTitle: 'Calendar connected',
    completedOnboardingListDescription: 'Your AI Assistant is in the right place at the right time — no setup before each call',
    completedBullets: CALENDAR_COMPLETED_BULLETS,
    completedLinks: [{ label: 'Set up custom Event filters', to: '/account/profile-settings/meeting-assistant' }],
  },
  [DefaultOnboardingFlowStepId.ZOOM]: {
    onboardingScreenDisplayOrder: 2,
    capability: Capability.ZOOM,
    icon: <i className='bi bi-camera-video' />,
    pendingOnboardingListTitle: 'Capture your meetings',
    pendingHeaderTitle: 'Connect Zoom',
    onboardingStepNotification:
      "Don't need to connect Google Meet and Microsoft Teams individually, just connect your calendar or invite the Assistant to your meetings.",
    pendingOnboardingListDescription: 'Connect Zoom, so your AI Assistant can join calls and generate notes automatically',
    pendingHeaderSubtitle: 'Integrate Zoom with Zocks to automatically capture meeting results, streamline workflow and enhance productivity.',
    completedOnboardingListTitle: 'Zoom connected',
    completedOnboardingListDescription: 'Zocks captures meeting results, streamlines your workflow and enhances your productivity',
    completedBullets: ZOOM_COMPLETED_BULLETS,
    completedLinks: [{ label: 'Set up custom Auto-join rules', to: '/account/profile-settings/meeting-assistant' }],
  },
  [DefaultOnboardingFlowStepId.CONTACT_SOURCE]: {
    onboardingScreenDisplayOrder: 3,
    capability: Capability.CONTACT_SOURCE,
    icon: <i className='bi bi-people' />,
    pendingOnboardingListTitle: 'Sync your contacts',
    pendingHeaderTitle: 'Connect your CRM',
    pendingOnboardingListDescription: "Pull in client data from your CRM so Zocks knows who you're meeting",
    pendingHeaderSubtitle: 'Zocks syncs your client data, so your AI Assistant can use it to prepare for meetings and personalize notes and follow-ups.',
    completedOnboardingListTitle: 'Contacts synced',
    completedOnboardingListDescription: 'Your AI Assistant can use client data to prepare for meetings and personalize notes and follow-ups',
    completedBullets: CRM_COMPLETED_BULLETS,
    completedLinks: [],
  },
  [DefaultOnboardingFlowStepId.FINANCIAL_PLANNING]: {
    onboardingScreenDisplayOrder: 4,
    capability: Capability.FINANCIAL_PLANNING,
    icon: <i className='bi bi-graph-up' />,
    pendingOnboardingListTitle: 'Manage financial plans',
    pendingHeaderTitle: 'Connect a Financial Planning tool',
    pendingOnboardingListDescription: 'Connect your financial planning tool to get more detailed meeting prep before each call',
    pendingHeaderSubtitle:
      'Zocks pulls client financial data into your meeting prep and syncs details captured in meetings back to your planning tool — so your plans stay current without extra work.',
    completedOnboardingListTitle: 'Financial Planning tool connected',
    completedOnboardingListDescription: 'Zocks pulls client financial data into your meeting prep and syncs back details captured in meetings',
    completedBullets: [
      'Client financial data surfaces in every meeting prep',
      'Details captured in meetings sync back to your planning tool',
      'Stay on top of planning tasks without manual updates',
    ],
    completedLinks: [],
  },
  [DefaultOnboardingFlowStepId.MAIL]: {
    onboardingScreenDisplayOrder: 5,
    capability: Capability.MAIL,
    icon: <i className='bi bi-envelope' />,
    pendingOnboardingListTitle: 'Send follow-ups',
    pendingHeaderTitle: 'Connect your email',
    pendingOnboardingListDescription: 'Connect your email to send client summaries and action items straight from Zocks',
    pendingHeaderSubtitle: 'Zocks uses your email to draft follow-ups after meetings and personalize meeting prep. You review everything before it sends.',
    completedOnboardingListTitle: 'Email connected',
    completedOnboardingListDescription: 'Zocks uses your email to draft follow-ups after meetings and personalize meeting prep',
    completedBullets: [
      'AI-drafted follow-ups sent directly from your inbox',
      'Meeting summaries and action items delivered automatically',
      'You review everything before it sends',
    ],
    completedLinks: [],
  },
}

export const POST_CONNECT_COPIES = {
  'calendar-event-filters': {
    headerTitle: 'Let your AI Assistant take notes',
    headerSubtitle:
      'Focus your meeting list by choosing which meetings to sync, and let your AI Assistant join your meetings, transcribe the conversation, and deliver structured notes — automatically.',
  },
  'crm-calendar-sync': {
    headerSubtitle:
      'Let Zocks sync your upcoming meetings, so your AI Assistant is in the right place at the right time — no setup before each call. Review the defaults below before moving on.',
  },
  'contact-sync': {
    headerSubtitle:
      'Zocks uses your CRM data to prepare for meetings, update contacts, and save tasks automatically. Review the defaults below before moving on.',
  },
  'calendar-success': {
    bullets: CALENDAR_COMPLETED_BULLETS,
  },
  'contact-success': {
    bullets: CRM_COMPLETED_BULLETS,
  },
  'zoom-settings': {
    headerTitle: 'Zoom connected',
    headerSubtitle: 'Set up Zoom and let your AI Assistant join your Zoom meetings automatically.',
  },
  'zoom-success': {
    bullets: ZOOM_COMPLETED_BULLETS,
  },
}

export const getCustomCompletedLink = (connectedIntegrations: IntegrationServiceId[], capability: Capability): OnboardingStepLink | null => {
  if (capability === Capability.CONTACT_SOURCE) {
    const firstContactSourceIntegration = ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.find((integration) => connectedIntegrations.includes(integration))
    if (firstContactSourceIntegration) {
      return {
        label: `Set up additional settings for ${getIntegrationDisplayName(firstContactSourceIntegration)} in Integrations`,
        to: `/account/integration-settings/integrations/${firstContactSourceIntegration}/settings`,
      }
    }
  }
  return null
}

export const getIntegrationSpecificCompletedStepTitle = (connectedIntegrations: IntegrationServiceId[]): string | null => {
  const firstConnectedIntegration = connectedIntegrations.length ? connectedIntegrations[0] : null
  if (!firstConnectedIntegration) {
    return null
  }
  return `${getIntegrationShortDisplayName(firstConnectedIntegration)} connected successfully`
}
